import { Component, OnInit, ElementRef, QueryList, ViewChildren, ViewChild, signal, computed } from '@angular/core';
import { FlowObjectType, FlowObjectInstance, FlowObjectInstanceState, FlowObjectInstanceStateDescription, FlowObjectTypeCategory } from '../../models/flow-object.model';
import { ActivatedRoute, Router } from '@angular/router';
import { FlowInstance, FlowInstanceState } from '../../models/flow.model';
import { BaseComponent } from '../../components/base/base.component';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Enums } from '../../shared/enums';
import { FlowInstanceService } from '../../services/flow-instance.service';
import { AuthService } from '../../services/auth.service';
import { FlowObjectInstanceService } from '../../services/flow-object-instance.service';
import { Utils } from '../../shared/utils';
import { Title } from '@angular/platform-browser';
import { CookieService } from 'ngx-cookie-service';
import { ConfirmationService } from 'primeng/api';
import { FlowObjectDefinitionService } from '../../services/flow-object-definition.service';
import { InputDataTaskApprove, OutputDataTaskGatewayRules } from '../../models/input-output-data.model';
import { TaskRules } from '../../shared/task-rules';
import { IMPORTS } from './_imports';
import { ErrorService } from '../../services/error.service';
import { AnnouncerService } from 'src/app/services/announcer.service';
import { Announcer } from 'src/app/models/announcer.model';
import { ConfigSchema } from '../../models/config-schema.model';

@Component({
    selector: 'flow-instance',
    templateUrl: './flow-instance.component.html',
    styleUrl: './flow-instance.component.scss',
    standalone: true,
    imports: IMPORTS
})
export class FlowInstanceComponent extends BaseComponent implements OnInit {
    // #region [ViewChild]
    @ViewChildren('wrapperRef') wrapperRef: QueryList<ElementRef>;
    @ViewChildren('taskHeaderSeparatorRef') taskHeaderSeparatorRef: QueryList<ElementRef>;
    @ViewChildren('taskContainerRef') taskContainerRef: QueryList<ElementRef>;
    @ViewChildren('secondGroupRef') secondGroupRef: QueryList<ElementRef>;
    @ViewChild('descriptionMessageRef') descriptionMessageRef: ElementRef;
    @ViewChild('descriptionAreaRef') descriptionAreaRef: ElementRef;
    // #endregion

    // #region [Type props]
    FlowObjectType = FlowObjectType;
    FlowObjectInstanceState = FlowObjectInstanceState;
    FlowObjectInstanceStateDescription = FlowObjectInstanceStateDescription;
    FlowObjectTypeCategory = FlowObjectTypeCategory;
    Enums = Enums;
    Utils = Utils;
    // #endregion

    // #region [props]
    lastFinishedTaskOuterIndex: number;
    intervalControl: any = null;
    isRedirected = false;
    conectaEnvironmentSegment = '';
    // #endregion

    // #region [signals]
    $model = signal(null as FlowInstance);
    $isBootstrapFinished = signal(false);
    $isShowingDescription = signal(false);
    $isCancellationTaskOpen = signal(false);
    $drawableFlowObjectInstances = signal([] as FlowObjectInstance[]);
    $flowObjectInstanceResultOnly = signal(null as FlowObjectInstance);
    $startInboundApiTimer = signal('??:??');
    $unitAcronym = signal(null as string);
    $organizationAcronym = signal(null as string);
    $patriarchAcronym = signal(null as string);
    $announcements = signal([] as Announcer[]);
    $visibleAnnouncements = signal(0);
    $isCancelFromImpedingTask = signal(false);
    $isFlowInstanceRelatedToUser = signal(null as boolean);
    $isEmbedded = signal(false);
    $isEdocs = signal(false);
    $isInstanceResultOnly = signal(false);
    $isFromConecta = signal(false);
    // #endregion

    // #region [computeds]
    $currentUser = computed(() => this.authService.$user());
    $showStartInboundApiTaskTimer = computed(() => {
        let flowObjectDefinition = this.$model()?.flowDefinition?.flowObjectDefinitions?.find(x => x.typeId == FlowObjectType.StartInboundApi);
        if (flowObjectDefinition == null) return false;
        let flowObjectInstance = this.$model()?.flowObjectInstances?.find(x => x.flowObjectDefinitionId == flowObjectDefinition.id);
        return flowObjectInstance?.stateId == FlowObjectInstanceState.Started;
    });
    $configSchema = computed(() => {
        const schemaRaw = this.$model()?.flowDefinition?.configSchema;
        if (!schemaRaw) return null;

        try {
            return JSON.parse(schemaRaw);
        } catch (e) {
            return null;
        }
    });
    $canShowCancelButton = computed(() => {
        const isFlowActive = this.$model()?.stateId < FlowInstanceState.Finished;
        const isFlowCancellable = this.$configSchema()?.hasCancellationByDeliberation === true;
        const isUserNotOwner = this.$model()?.ownerId != this.$currentUser()?.id;

        return isFlowActive
            && isUserNotOwner
            && !this.$isCancellationTaskOpen()
            && (
                isFlowCancellable
                || this.$isCancelFromImpedingTask()
            );
    });
    // #endregion

    constructor(
        route: ActivatedRoute,
        router: Router,
        spinner: NgxSpinnerService,
        toastr: ToastrService,
        public authService: AuthService,
        private confirmationService: ConfirmationService,
        private cookieService: CookieService,
        private titleService: Title,
        private flowInstanceService: FlowInstanceService,
        private flowObjectInstanceService: FlowObjectInstanceService,
        private flowObjectDefinitionService: FlowObjectDefinitionService,
        private errorService: ErrorService,
        private announcerService: AnnouncerService,
    ) {
        super(route, router, spinner, toastr);

        this.$isEmbedded.set(window.location.pathname.includes(Enums.PagesPaths.Embedded));
        this.$isEdocs.set(window.location.pathname.includes(Enums.PagesPaths.Edocs));
        this.$isInstanceResultOnly.set(window.location.pathname.includes(Enums.PagesPaths.FlowInstanceResult));
        this.isRedirected = this.route.snapshot.queryParamMap.get('d') == '1';

        if (atob(this.cookieService.get('prodest-eflow-cnc')).toLowerCase() == 'true') {
            this.$isFromConecta.set(window.location.pathname.includes(Enums.PagesPaths.CatalogService));
        }

        switch (atob(this.cookieService.get('prodest-eflow-env')).toLowerCase()) {
            case 'des':
                this.conectaEnvironmentSegment = 'dev.';
                break;

            case 'hom':
                this.conectaEnvironmentSegment = 'hom.';
                break;

            case 'tre':
                this.conectaEnvironmentSegment = 'hom.';
                break;
        }
    }

    async ngOnInit() {
        try {
            const response = await this.flowInstanceService.getById(this.paramId, Number.isNaN(this.paramVersion) ? null : this.paramVersion);

            if (!response.isSuccess) {
                this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());

                if (!this.$isEmbedded() && !this.$isEdocs()) {
                    this.router.navigate([Enums.PagesPaths.FlowInstanceList]);
                }

                return;
            }

            Utils.setSignal(this.$model, response.data);

            if (response.data == null) {
                this.$isFlowInstanceRelatedToUser.set(false);
                return;
            } else {
                this.$isFlowInstanceRelatedToUser.set(true);
            }

            if (
                this.$model().stateId >= FlowInstanceState.Finished
                && this.$isEmbedded()
                && !this.isRedirected
            ) {
                this.redirectToFlowInstanceResult();
            }

            if (this.$isInstanceResultOnly()) {
                if (this.$model().stateId < FlowInstanceState.Finished) {
                    this.redirectToFlowInstance();
                } else {
                    Utils.setSignal(this.$flowObjectInstanceResultOnly, this.$model().flowObjectInstances.find(x =>
                        x.stateId >= FlowInstanceState.Finished
                        && TaskRules.getFinisherTaskTypes().includes(x.flowObjectDefinition.typeId)
                    ));
                }
            }

            Utils.updSignal(this.$model, x => {
                x.flowObjectInstances.sort((a, b) => a.flowObjectDefinition.outerIndex - b.flowObjectDefinition.outerIndex);
                return x;
            });

            this.$drawableFlowObjectInstances.set(this.$model().flowObjectInstances.filter(x => {
                return (
                    x.flowObjectDefinition.typeId != FlowObjectType.GatewayPaths
                    || this.shouldShowTaskGatewayPaths(this.$model().flowObjectInstances.find(y => y.flowObjectDefinition.typeId == FlowObjectType.GatewayPaths))
                ) && [
                    FlowObjectInstanceState.Started,
                    FlowObjectInstanceState.AwaitingAction,
                    FlowObjectInstanceState.Finished,
                    FlowObjectInstanceState.ManuallyFinished,
                    FlowObjectInstanceState.AutomaticallyCancelled,
                    FlowObjectInstanceState.ManuallyCancelled,
                    FlowObjectInstanceState.CancelledByDeliberation
                ].includes(x.stateId);
            }));

            if (this.$drawableFlowObjectInstances().length == 1) {
                this.toggleDescription();
            }

            let startInboundApiTask = this.$drawableFlowObjectInstances().find(x => x.flowObjectDefinition.typeId == FlowObjectType.StartInboundApi);
            if (startInboundApiTask != null) {
                const response = await this.flowObjectDefinitionService.getFormSchema(startInboundApiTask.flowObjectDefinitionId);

                if (!response.isSuccess) {
                    this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                    return;
                }

                startInboundApiTask.flowObjectDefinition.formSchema = response.data;
            }

            this.$unitAcronym.set(this.$model().unitInfo.split(' - ')[0].trim());
            this.$organizationAcronym.set(this.$model().organizationInfo.split(' - ')[0].trim());
            this.$patriarchAcronym.set(this.$model().patriarchInfo.split(' - ')[0].trim());

            this.titleService.setTitle('{0} | {1} ({2}-{3}-{4})'
                .replaceAll('{0}', this.titleService.getTitle().split('|')[0].trim())
                .replaceAll('{1}', this.$model().name)
                .replaceAll('{2}', this.$patriarchAcronym())
                .replaceAll('{3}', this.$organizationAcronym())
                .replaceAll('{4}', this.$unitAcronym())
            );

            if (this.$model().stateId == FlowInstanceState.AutomaticallyCancelled) {
                Utils.updSignal(this.$drawableFlowObjectInstances, x => {
                    x.push({
                        finisherInfo: Enums.Messages.AutomaticallyCancelledFinisherInfo,
                        flowObjectDefinition: {
                            typeId: FlowObjectType.CancelBySchedule,
                            name: FlowObjectTypeCategory.get(FlowObjectType.CancelBySchedule),
                            outerIndex: this.$drawableFlowObjectInstances().length + 1
                        }
                    });
                    return x;
                });
            }

            this.setStartInboundApiTimer();
        } catch (error) {
            this.toastr.error(Enums.Messages.GeneralError, Enums.Messages.Error, Utils.getToastrErrorOptions());
            try {
                this.errorService.log({
                    url: error.url,
                    user: this.$currentUser() || null,
                    error: error,
                });
            } catch { }

            if (!this.$isEmbedded() && !this.$isEdocs()) {
                this.router.navigate([Enums.PagesPaths.FlowInstanceList]);
            }

            this.spinner.hide();
        } finally {
            setTimeout(() => {
                this.$isBootstrapFinished.set(true);
                setTimeout(() => {
                    let wrappers = this.wrapperRef.toArray();
                    if (wrappers.length > 0) {
                        wrappers.forEach(item => item.nativeElement.style.height = '100%');
                    }

                    if(this.$model()?.stateId === FlowInstanceState.CancelledByDeliberation) {
                        this.cancelFlowInstanceByDeliberation();
                    }

                    this.spinner.hide();

                    this.getAnnouncements();
                }, 500);
            }, 300);
        }
    }

    shouldKeepTaskBodyClosed(idx: number, flowObjectInstance: FlowObjectInstance): boolean {
        return idx < this.$drawableFlowObjectInstances().length - 1
            && ![
                FlowObjectInstanceState.Started,
                FlowObjectInstanceState.ManuallyCancelled,
            ].includes(flowObjectInstance.stateId)
            && !(
                flowObjectInstance.flowObjectDefinition.typeId == FlowObjectType.GatewayApprove
                && flowObjectInstance.inputData != null
                && (JSON.parse(flowObjectInstance.inputData) as InputDataTaskApprove).hasApproved == false
            )
            && !(
                flowObjectInstance.flowObjectDefinition.typeId == FlowObjectType.GatewayRules
                && flowObjectInstance.outputData != null
                && (JSON.parse(flowObjectInstance.outputData) as OutputDataTaskGatewayRules).hasApproved == false
            );
    }

    getDateString(flowObjectInstance: FlowObjectInstance): string {
        switch (flowObjectInstance.stateId) {
            case FlowObjectInstanceState.Started:
                return flowObjectInstance.startDate != null ? new Date(flowObjectInstance.startDate).toLocaleString() : '';

            case FlowObjectInstanceState.Finished:
            case FlowObjectInstanceState.ManuallyFinished:
            case FlowObjectInstanceState.AutomaticallyCancelled:
            case FlowObjectInstanceState.ManuallyCancelled:
            case FlowObjectInstanceState.CancelledByDeliberation:
                return flowObjectInstance.finishDate != null ? new Date(flowObjectInstance.finishDate).toLocaleString() : '';

            default:
                return new Date(flowObjectInstance.updateDate).toLocaleString();
        }
    }

    // controla as ações de exibição/recolhimento dos detalhes de cada Tarefa
    toggleTaskBody(event) {
        let toggleBodyElement = event.currentTarget.parentElement.querySelector('.toggle-body');
        if (toggleBodyElement != null) {
            toggleBodyElement.classList.contains('on') ? toggleBodyElement.classList.remove('on') : toggleBodyElement.classList.add('on');
        }

        let taskHeaderSeparator = this.taskHeaderSeparatorRef.toArray().find(x =>
            x.nativeElement.parentElement == event.composedPath().find(y => y.classList.contains('task-area'))
        );
        let taskContainer = this.taskContainerRef.toArray().find(x =>
            x.nativeElement.parentElement == event.composedPath().find(y => y.classList.contains('task-area'))
        );
        let taskArea = taskContainer.nativeElement.parentElement;

        if (taskContainer.nativeElement.classList.contains('d-none')) {
            taskHeaderSeparator?.nativeElement.classList.remove('d-none');
            taskContainer.nativeElement.classList.remove('d-none');
            taskArea.classList.remove('hidden-ish');
            setTimeout(() => taskContainer.nativeElement.classList.remove('hidden-ish'), 1);
        } else {
            taskContainer.nativeElement.classList.add('hidden-ish');
            taskArea.classList.add('hidden-ish');
            setTimeout(() => taskHeaderSeparator?.nativeElement.classList.add('d-none'), 100);
            setTimeout(() => taskContainer.nativeElement.classList.add('d-none'), 300);
        }
    }

    // controla as ações de exibição/recolhimento das informações do header de cada Tarefa
    toggleHeaderInfo(event) {
        event.currentTarget.classList.toggle('info-on');
        event.stopPropagation();

        let secondGroup = this.secondGroupRef.toArray().find(x =>
            x.nativeElement.parentElement == event.composedPath().find(y => y.classList.contains('task-header'))
        );

        secondGroup.nativeElement.parentElement.classList.toggle('info-on');
        secondGroup.nativeElement.parentElement.parentElement.classList.toggle('info-on');

        if (secondGroup.nativeElement.classList.contains('display-none')) {
            secondGroup.nativeElement.classList.remove('display-none');
            setTimeout(() => secondGroup.nativeElement.classList.remove('hidden-ish'), 1);
        } else {
            secondGroup.nativeElement.classList.add('hidden-ish');
            setTimeout(() => secondGroup.nativeElement.classList.add('display-none'), 100);
        }
    }

    async executeTask(flowObjectInstance: FlowObjectInstance) {
        this.confirmationService.confirm({
            message: Enums.Messages.ConfirmExecuteTask,
            accept: async () => {

                this.spinner.show('executing');
                const response = await this.flowObjectInstanceService.executeTask(flowObjectInstance);

                this.spinner.hide('executing');

                if (!response.isSuccess) {
                    this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                    this.reload();
                    return;
                }

                this.toastr.success(response.message.description, Enums.Messages.Success);
                this.reload();
            },
            reject: () => {
                this.confirmationService.close();
            }
        });
    }

    async cancelByDeliberation(flowObjectInstance: FlowObjectInstance) {
        this.confirmationService.confirm({
            message: Enums.Messages.ConfirmCancelByDeliberation,
            accept: async () => {
                this.spinner.show('executing');

                flowObjectInstance.flowInstanceId = this.$model().id;
                delete flowObjectInstance.finishDate;
                delete flowObjectInstance.finisherInfo;
                delete flowObjectInstance.flowObjectDefinition.id;
                delete flowObjectInstance.flowObjectDefinition.outerIndex;

                const response = await this.flowInstanceService.cancelByDeliberation(flowObjectInstance);

                this.spinner.hide('executing');

                if (!response.isSuccess) {
                    this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                    return;
                }

                this.toastr.success(response.message.description, Enums.Messages.Success);
                this.reload();
            }
        });
    }

    outboundApiChangeHandler(flowObjectInstance: FlowObjectInstance) {
        Utils.updSignal(this.$drawableFlowObjectInstances, xx => {
            let match = xx.find(x => x.id == flowObjectInstance.id);
            Object.assign(match, flowObjectInstance);
            return xx;
        });
    }

    getDescriptionHtml(): string {
        return this.$model()?.flowDefinition?.publishedDescription;
    }

    toggleDescription() {
        let interval = setInterval(() => {
            if (this.descriptionAreaRef == null) return;

            clearInterval(interval);

            this.$isShowingDescription.set(!this.$isShowingDescription());

            setTimeout(() => this.descriptionAreaRef.nativeElement.style.height = !this.$isShowingDescription()
                ? '0'
                : `calc(${this.descriptionMessageRef.nativeElement.clientHeight}px + ${this.$isEmbedded() ? '74px' : '50px'})`
            , 1);
        }, 300);
    }

    async setStartInboundApiTimer() {
        const response = await this.flowInstanceService.getServerTime();

        let serverNow = new Date(response).getTime();
        let localNow = new Date().getTime();
        let delta = localNow - serverNow;

        let interval = setInterval(() => {
            if (this.$model() == null) return;
            if (!this.$showStartInboundApiTaskTimer() || this.$startInboundApiTimer() == '00:00') {
                clearInterval(interval);
                return;
            }

            let start = new Date(this.$model().createDate).getTime();
            let now = new Date().getTime() - delta;
            let elapsedTime = Math.round((now - start) / 1000);
            let remainingTime = (5 * 60) - elapsedTime;
            remainingTime = remainingTime < 0 ? 0 : remainingTime;
            let seconds = remainingTime % 60;
            let minutes = Math.floor(remainingTime / 60);

            this.$startInboundApiTimer.set(`${minutes.toFixed(0).padStart(2, '0')}:${(seconds == 60 ? 0 : seconds).toFixed(0).padStart(2, '0')}`);
        }, 1000);
    }

    redirectToFlowInstance(comesFromResult?: boolean) {
        if (this.$isEmbedded()) {
            this.router.navigate(
                [Enums.PagesPaths.FlowInstance, this.paramId, Enums.PagesPaths.Embedded.slice(1)],
                comesFromResult
                    ? { queryParams: { d: 1 } }
                    : null
            );
        } else if (this.$isEdocs()) {
            this.router.navigate(
                [Enums.PagesPaths.FlowInstance, this.paramId, Enums.PagesPaths.Edocs.slice(1)],
                comesFromResult
                    ? { queryParams: { d: 1 } }
                    : null
            );
        } else {
            this.router.navigate(
                [Enums.PagesPaths.FlowInstance, this.paramId],
                comesFromResult
                    ? { queryParams: { d: 1 } }
                    : null
            );
        }
    }

    redirectToFlowInstanceResult() {
        if (this.$isEmbedded()) {
            this.router.navigate([Enums.PagesPaths.FlowInstanceResult, this.paramId, Enums.PagesPaths.Embedded.slice(1)]);
        } else if (this.$isEdocs()) {
            this.router.navigate([Enums.PagesPaths.FlowInstanceResult, this.paramId, Enums.PagesPaths.Edocs.slice(1)]);
        } else {
            this.router.navigate([Enums.PagesPaths.FlowInstanceResult, this.paramId]);
        }
    }

    backToEdocs() {
        const resolvedSubdomain = Utils.getResolvedEDocsSubdomain(this.cookieService);
        window.location.href = 'https://{0}es.gov.br/eflow/situacao/{1}{2}'
            .replaceAll('{0}', resolvedSubdomain)
            .replaceAll('{1}', this.paramId)
            .replaceAll('{2}', Utils.isNotNullOrEmpty(this.paramOrigin)
                ? `?${Enums.PagesPaths.Origin.slice(1)}=${this.paramOrigin}`
                : ''
            );
    }

    cancelFlowInstanceByDeliberation() {
        this.$isCancellationTaskOpen.set(true);

        const openContainers = this.taskContainerRef.toArray().filter(x =>
            !x.nativeElement.classList.contains('d-none')
        );

        openContainers.forEach(container => {
            const taskContainer = container.nativeElement;
            const taskArea = taskContainer.parentElement;
            const taskHeaderSeparator = taskArea.querySelector('.task-header-separator');
            const toggleIcon = taskArea.querySelector('.toggle-body');

            toggleIcon?.classList.remove('on');
            taskContainer.classList.add('hidden-ish');
            taskArea.classList.add('hidden-ish');

            setTimeout(() => taskHeaderSeparator?.classList.add('d-none'), 100);
            setTimeout(() => taskContainer.classList.add('d-none'), 300);
        });

        const model = this.$model();
        const isCancelled = model.stateId === FlowInstanceState.CancelledByDeliberation;

        const lastValidInstance = model.flowObjectInstances.find(x => x.stateId == FlowObjectInstanceState.Started)
            || model.flowObjectInstances.find(x => x.stateId == FlowObjectInstanceState.CancelledByDeliberation);
        const realDefinition = model.flowDefinition.flowObjectDefinitions.find(x => x.typeId == FlowObjectType.CancelByDeliberation);

        const cancelByDeliberationInstance = {
            flowObjectDefinition: {
                id: realDefinition?.id || null,
                typeId: FlowObjectType.CancelByDeliberation,
                name: FlowObjectTypeCategory.get(FlowObjectType.CancelByDeliberation),
                outerIndex: this.$drawableFlowObjectInstances().length + 1
            } as any,
            stateId: FlowObjectInstanceState.CancelledByDeliberation,
            inputData: lastValidInstance.inputData,
            flowObjectInstanceActors: lastValidInstance.flowObjectInstanceActors,
            flowInstanceId: model.id,
            finisherInfo: isCancelled ? model.cancellationActorInfo : null,
            finishDate: isCancelled ? model.finishDate : null,
        };

        Utils.updSignal(this.$drawableFlowObjectInstances, list => {
            return [...list, cancelByDeliberationInstance];
        });
    }

    discardManualCancel() {
        this.$isCancellationTaskOpen.set(false);

        Utils.updSignal(this.$drawableFlowObjectInstances, list => {
            return list.filter(inst => inst.flowObjectDefinition.typeId !== FlowObjectType.CancelByDeliberation);
        });

        setTimeout(() => {
            let tasks = document.querySelectorAll('.task-area') as any;
            [...tasks].find(x => x.querySelector('.task-order > span').innerText == tasks.length).querySelector('.task-name').click();
        }, 50);
    }

    shouldNotShowTaskBody(flowObjectInstance: FlowObjectInstance): boolean {
        return (
            flowObjectInstance.stateId == FlowObjectInstanceState.CancelledByDeliberation
            && flowObjectInstance.flowObjectDefinition.typeId != FlowObjectType.CancelByDeliberation
        ) || (
            flowObjectInstance.stateId == FlowObjectInstanceState.AutomaticallyCancelled
        ) ;
    }

    shouldShowTaskGatewayPaths(flowObjectInstance: FlowObjectInstance): boolean {
        let localConfigSchema = JSON.parse(flowObjectInstance.flowObjectDefinition.configSchema) as ConfigSchema;
        return localConfigSchema.taskGatewayPaths.isDivisionByDeliberation
            && (
                flowObjectInstance.stateId < FlowObjectInstanceState.Finished
                || (
                    flowObjectInstance.stateId >= FlowObjectInstanceState.Finished
                    && this.shouldLetPublicAgentInteractWith(flowObjectInstance)
                )
            );
    }

    // #region [===== private methods =====]
    // #endregion

    private reload() {
        if (this.$isEmbedded()) {
            this.router.navigateByUrl(`/${Enums.PagesPaths.LoadingPage}`, { skipLocationChange: true })
                .then(() => this.router.navigate([Enums.PagesPaths.FlowInstance, this.paramId, Enums.PagesPaths.Embedded.slice(1)]));
        } else if (this.$isEdocs()) {
            this.router.navigateByUrl(`/${Enums.PagesPaths.LoadingPage}`, { skipLocationChange: true })
                .then(() => this.router.navigate([Enums.PagesPaths.FlowInstance, this.paramId, Enums.PagesPaths.Edocs.slice(1)]));
        } else {
            this.router.navigateByUrl(`/${Enums.PagesPaths.LoadingPage}`, { skipLocationChange: true })
                .then(() => this.router.navigate([Enums.PagesPaths.FlowInstance, this.paramId]));
        }
    }

    private async getAnnouncements() {
        const announcements = await this.announcerService.get();
        this.$announcements.set(announcements);
        this.$visibleAnnouncements.set(announcements.length);
    }

    private shouldLetPublicAgentInteractWith(flowObjectInstance: FlowObjectInstance): boolean {
        return this.$currentUser()?.isPublicAgent
            && flowObjectInstance.flowObjectInstanceActors.some(x => x.actorId == this.$currentUser()?.id)
            && (
                this.$model()?.ownerId != this.$currentUser()?.id
                || this.$currentUser()?.papeis?.some(x =>
                    x.isCurrentUserHeadOfThisRolesOrganization
                    && x.lotacaoGuid.trim().toLowerCase() == this.$model()?.currentActionableLocationId.trim().toLowerCase()
                )
            );
    }
}
